import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import CopyCheckbox from './CopyCheckbox';

/* A parent row's checkbox sits after its 20px expand chevron, so a sub-calendar
   has to clear padding + chevron + gap before its extra indent shows at all. */
const ROW_PADDING = 8;
const ROW_GAP = 8;
const EXPAND_CONTROL_WIDTH = 20;
const SUB_CALENDAR_INDENT = 24;
const SUB_CALENDAR_PADDING = ROW_PADDING + EXPAND_CONTROL_WIDTH + ROW_GAP + SUB_CALENDAR_INDENT;

const OptionRow = styled('div')`
  display: flex;
  align-items: center;
  gap: ${ROW_GAP}px;
  min-height: 36px;
  padding: 0 ${ROW_PADDING}px 0 ${({ indented }) => (indented ? SUB_CALENDAR_PADDING : ROW_PADDING)}px;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceSecondary};
  }
`;

const SecondaryText = styled('span')`
  margin-left: auto;
  overflow: hidden;
  font-family: 'Nunito Sans';
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CopyCalendarOption = ({
  calendar, isChecked, onToggle, indented, secondaryText, leading,
}) => {
  const handleChange = () => {
    onToggle(calendar.id, !isChecked);
  };

  /* The checkbox and its label already toggle (a label click re-dispatches on
     the input, which bubbles here too), and the expand chevron has its own job -
     so only presses on the rest of the row toggle from here. */
  const handleRowClick = (e) => {
    if (e.target.closest('input, label, button')) {
      return;
    }
    handleChange();
  };

  return (
    // Keyboard users toggle through the checkbox itself; the row is a mouse target only.
    <OptionRow indented={indented} onClick={handleRowClick}>
      {leading}
      <CopyCheckbox
        checked={isChecked}
        id={`copy-target-calendar-${calendar.id}`}
        inputProps={{ 'data-picker-option': 'true' }}
        label={calendar.name}
        onChange={handleChange}
      />
      {secondaryText && <SecondaryText>{secondaryText}</SecondaryText>}
    </OptionRow>
  );
};

CopyCalendarOption.propTypes = {
  calendar: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  isChecked: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  indented: PropTypes.bool,
  secondaryText: PropTypes.string,
  leading: PropTypes.node,
};

CopyCalendarOption.defaultProps = {
  indented: false,
  secondaryText: '',
  leading: null,
};

export default CopyCalendarOption;
